<template>
  <div
    v-if="
      $hasAuthority('rateShop', 'readBenchmark') &&
      (loading || benchmarks.length)
    "
    class="rate-shop-benchmarks flex flex-gap-1"
  >
    <template v-if="loading">
      <!-- i is a counter here not an index, so it starts at 1 -->
      <KeyValuePair
        v-for="i in 3"
        :key="i"
        class="loading-benchmark border-color-divider"
        :class="{ 'border-left-1': i > 1 }"
      >
        <template #label>
          <Skeleton width="100px" height="1rem" class="bg-gray-darker" />
        </template>
        <template #value>
          <Skeleton width="100px" height="1rem" class="bg-gray-dark" />
        </template>
      </KeyValuePair>
    </template>
    <template v-else>
      <KeyValuePair
        v-for="(benchmark, i) in benchmarks"
        :key="benchmark.id"
        v-tooltip.top="{
          disabled: !benchmark.originalDATRate,
          value: datTooltip,
          class: 'dat-tooltip',
        }"
        :label="benchmark.contractName"
        :value="
          renderMoney(benchmark.totalPrice, {
            currencyCode: benchmark.currencyCode,
          })
        "
        :class="{ 'border-left-1': i > 0 }"
        class="border-color-divider"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import { computed, PropType, toRefs } from 'vue';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';

import { DATRateProviderDetails, RateQuote } from '@/model';

import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Skeleton from 'primevue/skeleton';

const props = defineProps({
  benchmarks: {
    type: Array as PropType<RateQuote[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { benchmarks, loading } = toRefs(props);
const vTooltip = useTooltip();
defineExpose({
  $primevue: usePrimeVue(),
});

const datData = computed((): DATRateProviderDetails | undefined => {
  const datBenchmark = benchmarks.value.find((b) => b.originalDATRate);
  return datBenchmark === undefined
    ? undefined
    : new DATRateProviderDetails(datBenchmark.rateProviderDetails);
});

const datTooltip = computed(() => {
  if (!datData.value) {
    return '';
  }

  return (
    `Rate Type: Spot\n` +
    `${datData.value.timeframe}\n` +
    `${datData.value.reports} reports (${datData.value.companies} companies)\n` +
    `${datData.value.origin} to ${datData.value.destination}`
  );
});
</script>

<style>
.rate-shop-benchmarks {
  background-color: var(--p-gray-100);
  border: 1px solid var(--p-gray-200);
  border-radius: var(--p-border-radius-sm);
  overflow-x: auto;

  .key-value-pair {
    padding: var(--sp-3);

    .label {
      color: var(--p-gray-600);
      font-size: 12px;
    }

    .value {
      color: var(--p-gray-400);
      font-size: 16px;
    }

    &.border-left-1 {
      padding-left: var(--sp-3);
      border-color: var(--p-gray-300);
    }
  }
}

.dat-tooltip {
  overflow: hidden;
  white-space: nowrap;
}
</style>
